import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowUpRight, Compass } from "lucide-react";
import { api } from "@/lib/api";
import { useSEO } from "@/lib/useSEO";
import { decideRobots, buildCanonical, buildPageMeta, slugifyTaxonomy } from "@/lib/seoIndex";
import OfferCard from "@/components/OfferCard";
import Breadcrumbs from "@/components/Breadcrumbs";
import { getTagExamples } from "@/data/comboTagExamples";

const SUPPORTED_LANGS = ["en", "de", "fr", "es"];

/**
 * Combo landing page: city × cause OR city × category.
 * Renders at /c/:citySlug/:tagSlug. Backend returns 404 for empty combos,
 * so we'll never publish thin-content pages.
 */
export default function CityComboPage() {
  const { citySlug, tagSlug } = useParams();
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [cityCauses, setCityCauses] = useState(null); // for Related section
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  /* eslint-disable */
  useEffect(() => {
    setLoading(true); setNotFound(false);
    api.get(`/c/${citySlug}/${tagSlug}`)
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [citySlug, tagSlug]);

  // Fetch the parent city's full by_cause dictionary so the
  // "Explore more causes in {city}" block can list real siblings.
  // (The combo endpoint's by_cause is naturally filtered to the chosen
  // cause and therefore unsuitable for the related section.)
  useEffect(() => {
    setCityCauses(null);
    api.get(`/cities/${citySlug}`)
      .then((r) => setCityCauses(r.data?.by_cause || {}))
      .catch(() => setCityCauses({}));
  }, [citySlug]);
  /* eslint-enable */

  const baseUrl = buildCanonical(`/c/${citySlug}/${tagSlug}`);
  const cityName = data?.city || (citySlug || "").replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  const localizedCity = data ? t(`cities.${data.city}`, { defaultValue: data.city }) : cityName;
  const localizedCountry = data ? t(`countries.${data.country}`, { defaultValue: data.country }) : "";
  const niceTag = data
    ? data.axis === "cause"
      ? t(`causes.${data.axis_value}`, { defaultValue: data.axis_value })
      : t(`categories.${data.axis_value}`, { defaultValue: data.axis_value })
    : (tagSlug || "").replace(/-/g, " ");

  const comboMeta = buildPageMeta("cityCombo", {
    cityName: localizedCity,
    causeName: niceTag,
    totalOffers: data?.total_offers,
  });

  useSEO({
    robots: decideRobots({ pageType: "cityCombo", totalOffers: data?.total_offers }),
    title: comboMeta.title,
    description: comboMeta.description,
    canonical: baseUrl,
    alternates: SUPPORTED_LANGS.map((l) => ({ hreflang: l, href: `${baseUrl}?lang=${l}` }))
      .concat([{ hreflang: "x-default", href: baseUrl }]),
    jsonLd: data
      ? {
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: `${niceTag} in ${localizedCity}`,
          url: baseUrl,
          mainEntity: {
            "@type": "ItemList",
            numberOfItems: data.total_offers,
            itemListElement: (data.top_offers || []).map((o, i) => ({
              "@type": "ListItem",
              position: i + 1,
              item: {
                "@type": "Organization",
                name: o.organization,
                url: o.url,
                description: (o.description || "").slice(0, 200),
              },
            })),
          },
        }
      : undefined,
  });

  if (loading) {
    return <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-16">
      <div className="h-10 w-2/3 bg-[#EFEAE0] rounded animate-pulse" />
    </div>;
  }
  if (notFound || !data) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="combo-not-found">
        <p className="text-3xl mb-2">🔍</p>
        <h1 className="text-3xl font-black">{t("city.not_found_title", { defaultValue: "Combination not found" })}</h1>
        <Link to={`/c/${citySlug}`} className="btn-primary mt-6 inline-flex">{t("city.browse_all", { defaultValue: "Browse all offers" })}</Link>
      </div>
    );
  }

  const filterParam = data.axis === "cause" ? "cause_area" : "category";

  return (
    <div data-testid="city-combo-page">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-[#F5F1E6] via-[#EFEAE0] to-[#E8EDDF]" />
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-14 lg:py-20">
          <Breadcrumbs crumbs={[
            { label: localizedCountry, to: `/country/${(data.country || "").toLowerCase().replace(/\s+/g, "-")}` },
            { label: localizedCity, to: `/c/${citySlug}` },
            { label: niceTag },
          ]} />
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight" data-testid="combo-h1">
            {niceTag} {t("combo.in", { defaultValue: "in" })} {localizedCity}
          </h1>
          <p className="mt-4 text-base md:text-lg text-[#3A463C] max-w-2xl">
            {t("combo.intro", {
              defaultValue:
                "{{count}} active {{tag}} opportunities in {{city}}, {{country}} — connect directly with local organisations.",
              count: data.total_offers,
              tag: niceTag.toLowerCase(),
              city: localizedCity,
              country: localizedCountry,
            })}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to={`/offers?city=${encodeURIComponent(data.city)}&${filterParam}=${encodeURIComponent(data.axis_value)}`}
              className="btn-primary"
              data-testid="combo-cta-see-all"
            >
              {t("combo.cta_see_all", { defaultValue: "See all {{count}}", count: data.total_offers })}
              <ArrowUpRight size={16} className="ml-1.5" strokeWidth={2} />
            </Link>
            <Link to={`/c/${citySlug}`} className="btn-pill border border-[#1F2A24] px-5 py-3 text-sm font-semibold inline-flex items-center bg-white hover:bg-[#F5F1E6]" data-testid="combo-back-city">
              {t("combo.back_to_city", { defaultValue: "More in {{city}}", city: localizedCity })}
            </Link>
          </div>
        </div>
      </section>

      {data.top_offers?.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-12">
          <p className="eyebrow">{t("city.section_top_eyebrow", { defaultValue: "Featured" })}</p>
          <h2 className="mt-2 text-2xl md:text-3xl font-black">
            {t("combo.top_title", { defaultValue: "Top {{tag}} in {{city}}", tag: niceTag.toLowerCase(), city: localizedCity })}
          </h2>
          <div className="mt-6 grid sm:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="combo-top-offers">
            {data.top_offers.map((o) => <OfferCard key={o.id} offer={o} />)}
          </div>
        </section>
      )}

      {/* Short useful intro — neutral, cause-specific, 80-120 words.
          No medical / tax / legal / political / impact claims. */}
      <section className="max-w-3xl mx-auto px-4 md:px-8 lg:px-12 py-10" data-testid="combo-intro">
        <p className="eyebrow">
          {t("combo.section_intro_eyebrow", { defaultValue: "About this page" })}
        </p>
        <h2 className="mt-2 text-2xl md:text-3xl font-black">
          {t("combo.section_intro_title", {
            defaultValue: "How to use this page",
          })}
        </h2>
        <div className="prose mt-4 max-w-none text-[#3A463C] leading-relaxed space-y-4 text-base">
          <p>
            {t("combo.intro_p1_v2", {
              defaultValue:
                "{{tag}} opportunities in {{city}} can include {{examples}}. This page lists active offers posted by organisations working in {{city}}, {{country}}.",
              tag: niceTag,
              city: localizedCity,
              country: localizedCountry,
              examples: getTagExamples(data.axis_slug || tagSlug),
            })}
          </p>
          <p>
            {t("combo.intro_p2", {
              defaultValue:
                "Listings link to each organisation's own page, where you can check current details, eligibility, location and next steps before applying or donating. Availability and contact preferences vary by organisation. If a listing no longer applies on the organisation's own site, it will drop from this page on the next refresh.",
            })}
          </p>
        </div>
      </section>

      {/* Related — other causes in the same city. Prioritises causes with
          enough active offers to justify a separate combo page (>= 3). */}
      {(() => {
        const sourceByCause = cityCauses || {};
        const others = Object.entries(sourceByCause)
          .filter(([cause, n]) => cause && n >= 3 && cause !== data.axis_value)
          .sort(([, a], [, b]) => b - a)
          .slice(0, 6);
        if (data.axis !== "cause" || others.length === 0) return null;
        return (
          <section className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-12" data-testid="combo-related-causes">
            <p className="eyebrow flex items-center gap-1.5">
              <Compass size={12} strokeWidth={2.5} />
              {t("combo.section_related_eyebrow", { defaultValue: "Related" })}
            </p>
            <h2 className="mt-2 text-2xl md:text-3xl font-black">
              {t("combo.section_related_title", {
                defaultValue: "Explore more causes in {{city}}",
                city: localizedCity,
              })}
            </h2>
            <div className="mt-6 grid sm:grid-cols-2 md:grid-cols-3 gap-3">
              {others.map(([cause, n]) => {
                const cSlug = slugifyTaxonomy(cause);
                const cLabel = t(`causes.${cause}`, { defaultValue: cause });
                return (
                  <Link
                    key={cause}
                    to={`/c/${citySlug}/${cSlug}`}
                    className="card-clay px-5 py-4 hover:shadow-md transition flex items-center justify-between gap-3"
                    data-testid={`combo-related-${cSlug}`}
                  >
                    <div className="min-w-0">
                      <p className="font-bold leading-snug">
                        {t("city.cause_combo_anchor", {
                          defaultValue: "{{cause}} opportunities in {{city}}",
                          cause: cLabel,
                          city: localizedCity,
                        })}
                      </p>
                      <p className="mt-1 text-xs text-[#5F6B61]">
                        {n} {n === 1
                          ? t("city.offer_singular", { defaultValue: "offer" })
                          : t("city.offer_plural", { defaultValue: "offers" })}
                      </p>
                    </div>
                    <ArrowUpRight size={16} strokeWidth={2} className="text-[#5B8C66] shrink-0" />
                  </Link>
                );
              })}
            </div>
          </section>
        );
      })()}

      {/* Explicit backlink to the city hub. The hero already has a button;
          this is the discovery-style cross-link near the foot of the page. */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-8" data-testid="combo-back-to-city-section">
        <Link
          to={`/c/${citySlug}`}
          className="card-clay px-5 py-4 hover:shadow-md transition flex items-center justify-between"
          data-testid="combo-back-to-city-link"
        >
          <div>
            <p className="font-bold leading-snug">
              {t("combo.backlink_label", {
                defaultValue: "All ways to help in {{city}}",
                city: localizedCity,
              })}
            </p>
            <p className="mt-1 text-xs text-[#5F6B61]">
              {t("combo.backlink_sub", {
                defaultValue: "Hub page for {{city}} — every cause, every way to help.",
                city: localizedCity,
              })}
            </p>
          </div>
          <ArrowUpRight size={16} strokeWidth={2} className="text-[#5B8C66]" />
        </Link>
      </section>

      {/* Trust/helpfulness — small, factual, data-backed only. */}
      <section className="max-w-3xl mx-auto px-4 md:px-8 lg:px-12 py-10" data-testid="combo-trust-block">
        <div className="border-t border-[#E5DECC] pt-6 text-sm text-[#5F6B61] leading-relaxed">
          <p>
            {t("combo.trust_intro", {
              defaultValue:
                "Browse active {{tag}} opportunities in {{city}}. Listings link to each organisation's own page, where you can check current details and next steps.",
              tag: niceTag.toLowerCase(),
              city: localizedCity,
            })}
          </p>
          <p className="mt-2">
            {t("city.trust_disclaimer", {
              defaultValue:
                "Availability, eligibility and next steps may vary by organisation. Updated from active listings.",
            })}
          </p>
        </div>
      </section>
    </div>
  );
}
